//
// Copyright (c) 2011-2020 by Curalate, Inc.
//
import { PojoMap } from 'pojo-maps';

import { isDefined } from '@utils/filterPredicates';

// Utility function to normalize data
// Follows the Redux pattern described here: https://redux.js.org/recipes/structuring-reducers/normalizing-state-shape

export interface Normalized<T extends {}, U extends PropertyKey> {
  byId: PojoMap<U, T>;
  orderedIds: U[];
}

/**
 * Given a list of item, create a normalized index of those items
 * for storing in redux.
 */
export default function normalize<T extends {}, U extends PropertyKey>(
  items: T[],
  indexSelector: (x: T) => U,
): Normalized<T, U> {
  // Save the ids only into an ordered array
  const orderedIds = items.map(indexSelector);
  // Build a record of items by id
  const byId = PojoMap.fromIndexing(items, indexSelector);

  return {
    byId,
    orderedIds,
  };
}

function empty<T extends {}, U extends PropertyKey>(): Normalized<T, U> {
  return {
    byId: PojoMap.empty(),
    orderedIds: [],
  };
}

export function normalizeToMap<T, U>(
  items: T[],
  indexSelector: (x: T) => U,
): {
  byId: Map<U, T>;
  orderedIds: U[];
} {
  // TODO: Look into Object.fromEntries after upgrading to Typescript 3.5
  const idItemPairs = items.map<[U, T]>((i) => [indexSelector(i), i]);
  const orderedIds = idItemPairs.map((pair) => pair[0]);
  const byId = new Map<U, T>(idItemPairs);
  return {
    byId,
    orderedIds,
  };
}

/**
 * Get an item from a normalized store.
 * (This is a typing helper to support lookups by tinytype)
 */
function getById<T extends {}, U extends PropertyKey>(
  store: Normalized<T, U>,
  id: U,
): T | undefined {
  return PojoMap.get(store.byId, id);
}

/**
 * Get a fully hydrated list of the items in a normalized store, in order.
 *
 * (This is a typing helper to handle null assertions safely)
 */
function getOrdered<T extends {}, U extends PropertyKey>(
  store: Normalized<T, U>,
): T[] {
  return store.orderedIds.map((id) => getById(store, id)).filter(isDefined);
}

/**
 * Remove a list of ids from the normalized store.
 * @param store Normalized Store
 * @param ids Array of ids to remove from the store
 * @returns a new store with the ids removed.
 */
function removeItems<T extends {}, U extends PropertyKey>(
  store: Normalized<T, U>,
  ids: readonly U[],
): Normalized<T, U> {
  const removeIds = new Set(ids);
  return {
    byId: PojoMap.fromEntries(
      PojoMap.entries(store.byId).filter(([id]) => !removeIds.has(id)),
    ),
    orderedIds: store.orderedIds.filter((id) => !removeIds.has(id)),
  };
}

/**
 * Remove an item from the normalized store by its id.
 * @param store Normalized Store
 * @param id id to remove from the store.
 * @returns a new store with the id removed.
 */
function removeItem<T extends {}, U extends PropertyKey>(
  store: Normalized<T, U>,
  id: U,
): Normalized<T, U> {
  return {
    byId: PojoMap.remove(store.byId, id),
    orderedIds: store.orderedIds.filter((existingId) => existingId !== id),
  };
}

/**
 * Add an item to the normalized store.
 * @param store Normalized Store
 * @param id The item's id.
 * @param item The item to add.
 * @returns A new store containing the original store and the new item.
 */
function addItem<T extends {}, U extends PropertyKey>(
  store: Normalized<T, U>,
  id: U,
  item: T,
): Normalized<T, U> {
  return {
    byId: PojoMap.set(store.byId, id, item),
    orderedIds: store.orderedIds.includes(id)
      ? store.orderedIds
      : [...store.orderedIds, id],
  };
}

/**
 * Add items to the normalized store.
 * @param store Normalized Store
 * @param items The normalized items to add.
 * @returns A new store containing the original store and the new items.
 */
function addItems<T extends {}, U extends PropertyKey>(
  store: Normalized<T, U>,
  items: Normalized<T, U>,
): Normalized<T, U> {
  return {
    byId: PojoMap.union(store.byId, items.byId),
    orderedIds: [
      ...store.orderedIds,
      ...items.orderedIds.filter((id) => !store.orderedIds.includes(id)),
    ],
  };
}

export const Normalized = {
  addItem,
  addItems,
  from: normalize,
  getById,
  getOrdered,
  empty,
  removeItems,
  removeItem,
};
